import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Activity, RefreshCw, ListOrdered, Megaphone, Clock } from 'lucide-react';

function StatChip({ label, value, color = 'text-slate-700' }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-slate-400">{label}</span>
      <span className={`font-mono font-semibold ${color}`}>{value}</span>
    </div>
  );
}

export function QueueMonitorPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/queue/status');
      setData(res);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (!autoRefresh) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  if (loading && !data) return <div className="p-12 text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="queue-monitor-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Queue Monitor</h2>
          <p className="text-sm text-slate-500 mt-1">
            Live operational view of in-flight campaign messages, sequences, and scheduler jobs.
            {data?.as_of && <span className="text-slate-400 ml-2">· as of {new Date(data.as_of).toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
            data-testid="queue-autorefresh-btn"
            className="gap-2"
          >
            <Activity className={`w-4 h-4 ${autoRefresh ? 'text-emerald-500' : ''}`} />
            Auto-refresh {autoRefresh ? 'on' : 'off'}
          </Button>
          <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="queue-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4" data-testid="queue-counters">
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-slate-400 text-[11px] uppercase tracking-wide">
            <Megaphone className="w-3.5 h-3.5" /> Campaign messages
          </div>
          <div className="mt-3 space-y-1.5">
            <StatChip label="Queued" value={data?.campaign_messages?.queued ?? 0} color="text-amber-600" />
            <StatChip label="Sent" value={data?.campaign_messages?.sent ?? 0} color="text-emerald-600" />
            <StatChip label="Failed" value={data?.campaign_messages?.failed ?? 0} color="text-rose-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-slate-400 text-[11px] uppercase tracking-wide">
            <ListOrdered className="w-3.5 h-3.5" /> Sequence runs
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">{data?.active_sequence_runs?.length ?? 0}</p>
          <p className="text-xs text-slate-500">currently active</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-slate-400 text-[11px] uppercase tracking-wide">
            <Clock className="w-3.5 h-3.5" /> Scheduler
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">{data?.scheduler_jobs?.length ?? 0}</p>
          <p className="text-xs text-slate-500">jobs registered</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="running-campaigns">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-slate-900">Running campaigns</h3>
        </div>
        {data?.running_campaigns?.length ? (
          <div className="divide-y divide-gray-100">
            {data.running_campaigns.map((c) => {
              const progress = c.audience_count > 0 ? Math.round((c.sent_count / c.audience_count) * 100) : 0;
              return (
                <div key={c.id} className="p-4 flex items-center gap-4" data-testid={`q-campaign-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 font-mono">{c.template_name}</p>
                  </div>
                  <div className="w-48">
                    <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div className="bg-[#00D1B2] h-full" style={{ width: `${progress}%` }}></div>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {c.sent_count}/{c.audience_count} sent · {c.queued_remaining} queued · {c.rate_per_minute}/min
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="p-6 text-xs text-slate-400 text-center">No running campaigns.</p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="active-sequence-runs">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-slate-900">Active sequence runs</h3>
        </div>
        {data?.active_sequence_runs?.length ? (
          <div className="divide-y divide-gray-100">
            {data.active_sequence_runs.slice(0, 20).map((r) => (
              <div key={r.id} className="p-3 flex items-center justify-between text-xs">
                <span className="font-mono text-slate-700">{r.phone}</span>
                <span className="text-slate-500">step {r.current_step + 1}</span>
                <span className="text-slate-400">next: {r.next_run_at ? new Date(r.next_run_at).toLocaleString() : '—'}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="p-6 text-xs text-slate-400 text-center">No active runs.</p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="scheduler-jobs">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-slate-900">Scheduler jobs</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {(data?.scheduler_jobs || []).map((j) => (
            <div key={j.id} className="p-3 flex items-center justify-between text-xs">
              <span className="font-mono text-slate-700">{j.id}</span>
              <span className="text-slate-500">next: {j.next_run_at ? new Date(j.next_run_at).toLocaleString() : '—'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
